import React from "react";
import { Editor } from "@tinymce/tinymce-react";
import { Editor as IEditor} from "tinymce";

import { userAttributesSvg } from "modules/common/icons/userAttributesSvgString";
import { EMAIL_PERSONALIZATION_TOKENS, IPersonalizationToken, MERGE_TAG_PREFIX, MERGE_TAG_SUFFIX } from "modules/emails/models";
import { addCustomDescriptionsToPersonalizationTokens } from "modules/common/components/authoring/helpers/personalizationTokenHelpers";

import "./styles/smsEditor.sass";

interface ISMSEditorProps {
    placeholder?: string;
    disclaimer?: string | JSX.Element;
    onChange: (plaintext: string, html: string) => void;
    onEditorUpdate?: (text: string) => void;
    value: string;
    maxTextLength: number;
    initialTextLength: number;
    disabled: boolean;
    id: string;
    externalEditorRef?: React.MutableRefObject<IEditor | null>;
    useExternalEditorRef: boolean;
}

const SmsEditor: React.FC<ISMSEditorProps> = ({
    placeholder,
    disclaimer,
    onChange,
    value,
    maxTextLength,
    initialTextLength,
    onEditorUpdate,
    disabled,
    id,
    externalEditorRef,
    useExternalEditorRef
}) => {

    const [htmlBody, setHtmlBody] = React.useState(value);
    const [letterCount, setLetterCount] = React.useState(initialTextLength);
    const [isFocused, setIsFocused] = React.useState(false);
    const atMaxText = letterCount >= maxTextLength;

    const internalEditorRef = React.useRef<IEditor | null>(null);
    const editorRef = (useExternalEditorRef && externalEditorRef?.current) ? externalEditorRef : internalEditorRef;

    const setup = (editor: IEditor) => {
        editorRef.current = editor;

        setupPersonalizationMenuItems(editor);
        setupWordCountLimit(editor);
    }

    const setupWordCountLimit = (editor: IEditor) => {
        editor.on("keydown", (e) => {
            const currentTextLength = getCurrentPlaintext(editorRef.current!).replaceAll("\n", ' ').length;
            const selectedSectionLength = editor.selection.getContent({ format: 'text' }).length;

            const userAddingCharacterOverLimit = currentTextLength >= maxTextLength && (e.key !== 'Backspace' && selectedSectionLength === 0);

            if(userAddingCharacterOverLimit) {
                e.preventDefault();
                e.stopPropagation();
                return false;
            }
        })
    }

    const checkAndInsertToken = (editor: IEditor, token: IPersonalizationToken, currentTextLength?: number, maxTextLength?: number) => {
        const tokenPlaintextLength = `${MERGE_TAG_PREFIX}${token.value}${MERGE_TAG_SUFFIX}`.length;

        if(!!maxTextLength && !!currentTextLength) {
            if(currentTextLength + tokenPlaintextLength <= maxTextLength)
                editor.insertContent(`<span class="mce-mergetag" contenteditable="false" data-mce-cef-wrappable="true" data-mce-mergetag="1"><span class="mce-mergetag-affix" data-mce-mergetag-affix>${MERGE_TAG_PREFIX}</span>${token.value}<span class="mce-mergetag-affix" data-mce-mergetag-affix>${MERGE_TAG_SUFFIX}</span></span>`);
        }
        else {
            editor.insertContent(`<span class="mce-mergetag" contenteditable="false" data-mce-cef-wrappable="true" data-mce-mergetag="1"><span class="mce-mergetag-affix" data-mce-mergetag-affix>${MERGE_TAG_PREFIX}</span>${token.value}<span class="mce-mergetag-affix" data-mce-mergetag-affix>${MERGE_TAG_SUFFIX}</span></span>`);
        }
    }

    const createPersonalizationTokensMenuButtons = (editor: IEditor) => {
        const tokenLength = EMAIL_PERSONALIZATION_TOKENS.length;

        const menuItems = EMAIL_PERSONALIZATION_TOKENS.map(token => ({
            type: "menuitem",
            text: token.title,
            onAction: () => checkAndInsertToken(editor, token, getCurrentPlaintext(editorRef.current!).replaceAll("\n", ' ').length, maxTextLength),
            onSetup: () => {
                addCustomDescriptionsToPersonalizationTokens(token.title, token.description, tokenLength);
            }
        }));
    
        return menuItems;
    };

    //Tiny doesn't provide a type for the callback inside, may need to leave these types ambiguous. 
    const setupPersonalizationMenuItems = (editor) => {
        editor.ui.registry.addIcon("userAttributes", userAttributesSvg);

        editor.ui.registry.addMenuButton('customMergeTags', {
            icon: 'userAttributes',
            text: ' ',
            fetch: (callback) => {
                const items = createPersonalizationTokensMenuButtons(editor);
                callback(items);
            }
        });

        editor.ui.registry.addNestedMenuItem('customMergeTags', {
            icon: 'userAttributes',
            text: 'Personalization token',
            getSubmenuItems: createPersonalizationTokensMenuButtons
        });
    }

    const onUpdateText = (_, editor: IEditor) => {
        onChange(getCurrentPlaintext(editor), htmlBody);
    }

    const onEditorChange = (text: string) => {
        const currentTinyText = getCurrentPlaintext(editorRef.current!);

        //This is specifically to catch when the user might try and insert a mergetag over the limit.
        if(currentTinyText.length > maxTextLength) {
            return;
        }
        
        setHtmlBody(text);
        setLetterCount(currentTinyText.replaceAll("\n", ' ').length);

        if(onEditorUpdate)
            onEditorUpdate(currentTinyText);
    }

    const getCurrentPlaintext = (editor: IEditor): string => {
        return editor.getContent({ format: 'text' });
    }

    return (
        <>
            <Editor
                init={{
                    object_resizing: false,
                    image_title: true,
                    menubar: false,
                    branding: false,
                    statusbar: false,
                    paste_block_drop: true,
                    powerpaste_block_drop: true,
                    force_br_newlines: true,
                    paste_as_text: true,
                    highlight_on_focus: true,
                    paste_preprocess(editor, args) {
                        //Need to calculate it here instead of using outside value as it'll snapshot the letter count on init otherwise.
                        let currentCount = getCurrentPlaintext(editor).replaceAll("\n", ' ').length
                        if(args.content.length + currentCount > maxTextLength) {
                            args.content = '';
                        }
                    },
                    plugins: ['emoticons', EMAIL_PERSONALIZATION_TOKENS.length > 0 ? "mergetags" : ""],
                    noneditable_noneditable_class: 'mceNonEditable',
                    toolbar: 'customMergeTags emoticons',
                    value: value,
                    width: '100%',
                    height: '253px',
                    paddingtop: '10px',
                    placeholder: placeholder,
                    content_style: "body { min-height: 100px; font-family: Roboto, Helvetica, Arial, sans-serif; }" +
                        ".mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before { color: #aaaaaa }" +
                        ".tox .tox-edit-area::before {border: 2px solid #b378ab}" +
                        ".mce-content-body[data-mce-placeholder] {white-space: pre}",
                    setup,
                    mergetags_prefix: MERGE_TAG_PREFIX,
                    mergetags_suffix: MERGE_TAG_SUFFIX,
                    mergetags_list: EMAIL_PERSONALIZATION_TOKENS
                }}
                id={id}
                value={htmlBody}
                onEditorChange={onEditorChange}
                initialValue={value}
                onBlur={onUpdateText}
                onFocusIn={() => setIsFocused(true)}
                onFocusOut={() => setIsFocused(false)}
                disabled={disabled}
            />
            {disclaimer && <span className="sms-editor-disclaimer">{disclaimer}</span>}
            <div className="sms-editor-word-count" style={{color: (atMaxText && isFocused)  ? "#b72026" : "#7F7F7F"}}>
                {letterCount} / {maxTextLength}
            </div>
        </>
    )
}

export default SmsEditor;